import { Color, ColorFill, Container, SvgGraphics } from '@tencent/alloy-renderer';
import { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import { isMetaOrShiftOrCtrlKey } from '@src/utils/key-mouse-event/mouse-event-check';
import { resizeCol, resizeRow } from './cursor';
import { updateWrapperTransform } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/utils/update-wrapper-transform';
import { getTopContainer } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/utils/get-top-container';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';

enum BorderType {
    col = 'col',
    row = 'row',
}

export class RowColResizeComponent extends Container {
    private cachedResizeBorders: Map<string, SvgGraphics>;

    private resizeIndicator: Container | null;

    private selectedItem;

    private beginX = 0;
    private beginY = 0;
    private offset = 0;

    private clickType?: BorderType;
    private isMoving = false;

    public constructor(private factoryOptions: InterfaceFactoryOptions<ITableModel>) {
        super();
        this.factoryOptions = factoryOptions;
        this.cachedResizeBorders = new Map();
    }

    public updateComponent(): void {
        const hitColor = new ColorFill(Color.Transparent);
        const scale = this.factoryOptions.getPageScale();

        const { model } = this.factoryOptions;
        const resueKeys = Array.from(this.cachedResizeBorders.keys());

        // eslint-disable-next-line no-restricted-syntax
        for (const key in model.tableBorders) {
            if (Object.prototype.hasOwnProperty.call(model.tableBorders, key)) {
                const item = model.tableBorders[key];
                if (key.startsWith('h_border_')) {
                    this.addHorizontalBorder(item, hitColor, scale, resueKeys);
                } else if (key.startsWith('v_border_')) {
                    this.addVerticalBrder(item, hitColor, scale, resueKeys);
                }
            }
        }

        resueKeys.forEach((key) => {
            const selector = this.cachedResizeBorders.get(key);
            if (selector) {
                this.removeChild(selector);
                this.cachedResizeBorders.delete(key);
            }
        });
    }

    private addHorizontalBorder(item, hitColor, scale, resueKeys): void {
        let resizeBorder = this.cachedResizeBorders.get(item.key);
        if (!resizeBorder) {
            resizeBorder = new SvgGraphics();
            resizeBorder.fill = hitColor;
            resizeBorder.onMouseDown(this.rowResizeMouseDown);
            this.addChild(resizeBorder);

            this.cachedResizeBorders.set(item.key, resizeBorder);
        } else {
            resueKeys.splice(resueKeys.indexOf(item.key), 1);
        }

        resizeBorder.cursor = this.factoryOptions.isEditable ? resizeRow : null;

        resizeBorder.x = item.left;
        if (this.factoryOptions.model.selected) {
            if (item.top === 0) {
                resizeBorder.y = item.top + (3 / scale);
            } else if (item.top === this.factoryOptions.model.props.style.height) {
                resizeBorder.y = item.top - (9 / scale);
            } else {
                resizeBorder.y = item.top - (3 / scale);
            }
        } else {
            resizeBorder.y = item.top - (3 / scale);
        }

        resizeBorder.path = `M 0 0 L ${item.width} 0 L ${item.width} ${6 / scale} L 0 ${6 / scale} Z`;

        Object.assign(resizeBorder, { item });
    }

    private addVerticalBrder(item, hitColor, scale, resueKeys): void {
        let resizeBorder = this.cachedResizeBorders.get(item.key);
        if (!resizeBorder) {
            resizeBorder = new SvgGraphics();
            resizeBorder.fill = hitColor;
            resizeBorder.onMouseDown(this.colResizeMouseDown);
            this.addChild(resizeBorder);

            this.cachedResizeBorders.set(item.key, resizeBorder);
        } else {
            resueKeys.splice(resueKeys.indexOf(item.key), 1);
        }

        resizeBorder.cursor = this.factoryOptions.isEditable ? resizeCol : null;

        if (this.factoryOptions.model.selected) {
            if (item.left === 0) {
                resizeBorder.x = item.left + (3 / scale);
            } else if (item.left === this.factoryOptions.model.props.style.width) {
                resizeBorder.x = item.left - (9 / scale);
            } else {
                resizeBorder.x = item.left - (3 / scale);
            }
        } else {
            resizeBorder.x = item.left - (3 / scale);
        }
        resizeBorder.y = item.top;

        resizeBorder.path = `M 0 0 L ${6 / scale} 0 L ${6 / scale} ${item.height} L 0 ${item.height} Z`;

        Object.assign(resizeBorder, { item });
    }

    /**
     * 将某component插入到某指定component的后面
     * @param insertComponent 要插入的component
     * @param afterWhich 目标位置的component
     */
    private componentAppendAfter = (insertComponent: Container, afterWhich: Container) => {
        const topContainer = getTopContainer(afterWhich);
        const elementsLayer = topContainer?.parent;
        if (elementsLayer) {
            const index = elementsLayer.children.findIndex(item => item === topContainer);
            elementsLayer.addChildAt(insertComponent, index + 1);
        }
    };

    /**
     * 创建模拟table，table内插入拖拽线（没直接放在原table下是为了避免引发原table的重新渲染）
     * @param type
     */
    private makeResizeIndicator = (type: BorderType) => {
        const scale = this.factoryOptions.getPageScale();
        const { height, width } = this.factoryOptions.model.props.style;
        let resizeIndicator = new Container();
        resizeIndicator = updateWrapperTransform(resizeIndicator, this.factoryOptions);
        const inner = new SvgGraphics();
        inner.stroke = new ColorFill(Color.fromHex('#cccccc'));
        inner.strokeWidth = 1 / scale;
        inner.cursor = type === BorderType.row ? resizeRow : resizeCol;
        inner.path = type === BorderType.row ? `M 0 0 L ${width} 0` : `M 0 0 L 0 ${height}`;
        if (type === BorderType.row) {
            inner.y = this.selectedItem.top;
        } else {
            inner.x = this.selectedItem.left;
        }
        resizeIndicator.addChild(inner);
        return resizeIndicator;
    };

    private colResizeMouseDown = (e) => {
        if (!this.factoryOptions.isEditable) {
            return;
        }
        if (this.factoryOptions.editingStateManager) {
            this.factoryOptions.editingStateManager.needDisableIframeEvent = true;
        }
        window.addEventListener('mousemove', this.handleMouseMove);
        window.addEventListener('mouseup', this.handleMouseUp);

        this.handleSelectTable(e);
        this.beginX = e.nativeEvent.clientX;
        this.selectedItem = e.target.item;
        this.clickType = BorderType.col;
    };

    private rowResizeMouseDown = (e) => {
        if (!this.factoryOptions.isEditable) {
            return;
        }
        if (this.factoryOptions.editingStateManager) {
            this.factoryOptions.editingStateManager.needDisableIframeEvent = true;
        }
        window.addEventListener('mousemove', this.handleMouseMove);
        window.addEventListener('mouseup', this.handleMouseUp);

        this.handleSelectTable(e);
        this.beginY = e.nativeEvent.clientY;
        this.selectedItem = e.target.item;
        this.clickType = BorderType.row;
    };

    private showIndicator() {
        this.factoryOptions.editingStateManager?.updateTableRowColResizeState(true);
        if (!this.resizeIndicator) {
            // 创建模拟table，table内插入拖拽线
            this.resizeIndicator = this.makeResizeIndicator(this.clickType!);
            // 将此模拟table挂载到真实table的同级（父节点为切片层）
            this.componentAppendAfter(this.resizeIndicator, this);
        }
    }

    // 列调整拖拽
    private colIndicatorMove = (e) => {
        const scale = this.factoryOptions.getPageScale();
        const { colWidths } = this.factoryOptions.model.props;
        // 操纵的纵向边索引
        const operateLineIndex = parseInt((this.selectedItem.key as string).substring(this.selectedItem.key.indexOf('col') + 3), 10);
        // 实际要变更宽度的目标列索引
        const destResizeColIndex = Math.max(0, operateLineIndex - 1);
        // 目标列的宽度
        const destResizeColWidth = colWidths[destResizeColIndex];
        // 目标列右侧列的宽度（也就是操纵的纵线边右侧的列）
        const rightColWidth = colWidths[destResizeColIndex + 1];
        let maxOffset = e.clientX - this.beginX;
        if (operateLineIndex === 0) {
            // 操纵的左边界线 - 向右有最小宽度，向左无限制
            maxOffset = Math.min(maxOffset, (destResizeColWidth - 40) * scale);
            this.offset = -maxOffset;
        } else if (operateLineIndex === colWidths.length) {
            // 操纵的右边界线 - 向左有最小宽度，向右无限制
            maxOffset = Math.max(maxOffset, -(destResizeColWidth - 40) * scale);
            this.offset = maxOffset;
        } else {
            // 操纵的中间的某个边 - 向左向右均有限制
            const leftEdge = -(destResizeColWidth - 40) * scale;
            const rightEdge = (rightColWidth - 40) * scale;
            maxOffset = Math.min(rightEdge, Math.max(maxOffset, leftEdge));
            this.offset = maxOffset;
        }
        if (this.resizeIndicator?.children?.[0]) {
            this.resizeIndicator.children[0].x = this.selectedItem.left + (maxOffset / scale);
        }
    };

    // 行调整拖拽
    private rowIndicatorMove = (e) => {
        const scale = this.factoryOptions.getPageScale();
        const startIndex = this.selectedItem.key.indexOf('row') + 3;
        const endIndex = this.selectedItem.key.indexOf('col');
        const row = parseInt((this.selectedItem.key as string).substring(startIndex, endIndex), 10) - 1;
        const rowHeight = this.factoryOptions.model.displayRowHeights[Math.max(0, row)];
        let maxOffset = e.clientY - this.beginY;
        if (row < 0) {
            maxOffset = Math.min(maxOffset, (rowHeight - 20) * scale);
            this.offset = -maxOffset;
        } else {
            maxOffset = Math.max(maxOffset, -(rowHeight - 20) * scale);
            this.offset = maxOffset;
        }
        if (this.resizeIndicator?.children?.[0]) {
            this.resizeIndicator.children[0].y = this.selectedItem.top + (maxOffset / scale);
        }
    };

    private handleMouseMove = (e): void => {
        if (!this.isMoving) {
            this.isMoving = true;
            this.showIndicator();
        }

        if (this.selectedItem && this.resizeIndicator) {
            if (this.clickType === BorderType.col) {
                this.colIndicatorMove(e);
            } else if (this.clickType === BorderType.row) {
                this.rowIndicatorMove(e);
            }
        }
    };

    private handleMouseUp = (): void => {
        if (this.factoryOptions.editingStateManager) {
            this.factoryOptions.editingStateManager.needDisableIframeEvent = false;
        }
        this.factoryOptions.editingStateManager?.updateTableRowColResizeState(false);
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('mouseup', this.handleMouseUp);
        const scale = this.factoryOptions.getPageScale();
        if (this.resizeIndicator) {
            this.resizeIndicator.parent?.removeChild(this.resizeIndicator);
            this.resizeIndicator.dispose();
            this.resizeIndicator = null;
            if (Math.abs(this.offset) >= 1) {
                if (this.clickType === BorderType.col) {
                    const { colWidths } = this.factoryOptions.model.props;
                    const startIndex = this.selectedItem.key.indexOf('col') + 3;
                    const operateLineIndex = parseInt((this.selectedItem.key as string).substring(startIndex), 10);
                    const destResizeColWidth = Math.max(0, operateLineIndex - 1);
                    const newCurWidth = colWidths[destResizeColWidth] + (this.offset / scale);
                    this.factoryOptions.model.resizeCol(destResizeColWidth, newCurWidth, operateLineIndex === 0);
                } else if (this.clickType === BorderType.row) {
                    const startIndex = this.selectedItem.key.indexOf('row') + 3;
                    const endIndex = this.selectedItem.key.indexOf('col');
                    const row = parseInt((this.selectedItem.key as string).substring(startIndex, endIndex), 10) - 1;
                    const newHeight = this.factoryOptions.model.displayRowHeights[Math.max(0, row)]
                        + (this.offset / scale);
                    this.factoryOptions.model.resizeRow(Math.max(0, row), newHeight, row < 0);
                }
            }
        }
        this.selectedItem = null;
        this.beginX = 0;
        this.beginY = 0;
        this.offset = 0;
        this.isMoving = false;
        this.clickType = undefined;
    };

    private handleSelectTable = (e): void => {
        e.stopPropagation();
        e.preventDefault();
        if (!isMetaOrShiftOrCtrlKey(e.nativeEvent)) {
            const { logicService } = this.factoryOptions;
            const tableEditLogic = logicService.getLogic(this.factoryOptions) as any;
            tableEditLogic.makeElementSelected(e.nativeEvent as MouseEvent);
        }
    };
}
